import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MODELOS, type ModeloId, type Pt } from './engine'
import {
  areaFechada,
  chaveDaParede,
  codigoDoChao,
  CORES_DE_CHAO,
  CORES_DE_PAREDE,
  corDe,
  mapaDeParedes,
  MATERIAIS,
  materialDe,
  proximaVaga,
  salaDaCelula,
  TIPOS_DE_PAREDE,
  type Material,
  type Parede,
  type Planta,
  type TipoDeParede,
} from './planta'
import './construcao.css'

/**
 * Modo construção da planta livre (SPEC-20261011-2023): chão, paredes, aberturas e salas.
 * O desenho acontece por cima do tabuleiro (camada com a grade e a prévia) e cada mudança
 * vai para o servidor sozinha, com desfazer e refazer.
 */

export type Ferramenta = 'chao' | 'parede' | 'apagar' | 'sala' | 'mao'
type FormaChao = 'pincel' | 'retangulo' | 'balde'
type FormaParede = 'linha' | 'caixa'
type Previa = { celulas?: number[]; paredes?: [number, number, 0 | 1][]; apagar?: boolean; cor?: string } | null
type Gesto =
  | { tipo: 'chao'; inicio: number; celulas: Set<number> }
  | { tipo: 'parede' | 'apagar'; a: Pt; b: Pt; moveu: boolean; ponto: Pt }
  | null
type DialogoDeSala = { celulas: number[]; x: number; y: number; existente: number | null } | null

const NOMES_DOS_MODELOS: Record<ModeloId, string> = { reuniao: 'Reunião', time: 'Time', estudio: 'Estúdio', copa: 'Copa', chefia: 'Diretoria', cabine: 'Sala 1:1' }
const CORES_DAS_SALAS = ['#ffb020', '#2fb36d', '#3f6fe0', '#e0679b', '#8a5be0', '#1f9e8a', '#d9822b']

/* ------------------------------------------------- mudanças na planta --- */

const comChao = (p: Planta, celulas: Iterable<number>, codigo: string | null): Planta => {
  const chao = [...p.chao]
  for (const c of celulas) if (c >= 0 && c < chao.length) chao[c] = codigo
  return { ...p, chao }
}
function trecho(p: Planta, a: Pt, b: Pt, caixa: boolean): [number, number, 0 | 1][] {
  const [ax, ay] = a
  const [bx, by] = b
  const out: [number, number, 0 | 1][] = []
  const linhaH = (x0: number, x1: number, y: number) => {
    for (let x = Math.min(x0, x1); x < Math.max(x0, x1); x++) if (x >= 0 && x < p.w && y >= 0 && y <= p.h) out.push([x, y, 0])
  }
  const linhaV = (x: number, y0: number, y1: number) => {
    for (let y = Math.min(y0, y1); y < Math.max(y0, y1); y++) if (y >= 0 && y < p.h && x >= 0 && x <= p.w) out.push([x, y, 1])
  }
  if (caixa && ax !== bx && ay !== by) {
    linhaH(ax, bx, ay)
    linhaH(ax, bx, by)
    linhaV(ax, ay, by)
    linhaV(bx, ay, by)
  } else if (Math.abs(bx - ax) >= Math.abs(by - ay)) linhaH(ax, bx, ay)
  else linhaV(ax, ay, by)
  return out
}
/** A linha da grade mais perto do ponto (para clique numa parede só). */
function linhaMaisPerto(p: Planta, [x, y]: Pt): [number, number, 0 | 1] | null {
  const fx = x - Math.floor(x)
  const fy = y - Math.floor(y)
  const dh = Math.min(fy, 1 - fy)
  const dv = Math.min(fx, 1 - fx)
  if (Math.min(dh, dv) > 0.35) return null
  if (dh <= dv) {
    const lx = Math.floor(x)
    const ly = Math.round(y)
    return lx >= 0 && lx < p.w && ly >= 0 && ly <= p.h ? [lx, ly, 0] : null
  }
  const lx = Math.round(x)
  const ly = Math.floor(y)
  return lx >= 0 && lx <= p.w && ly >= 0 && ly < p.h ? [lx, ly, 1] : null
}
function comParedes(p: Planta, linhas: [number, number, 0 | 1][], tipo: TipoDeParede | null, cor: string | null): Planta {
  const m = mapaDeParedes(p)
  for (const [x, y, o] of linhas) {
    const k = chaveDaParede(x, y, o)
    if (tipo === null) m.delete(k)
    else m.set(k, [x, y, o, tipo, tipo === 'vidro' ? null : cor] as Parede)
  }
  return { ...p, paredes: [...m.values()] }
}
function balde(p: Planta, inicio: number): number[] {
  const alvo = p.chao[inicio] ?? null
  const paredes = mapaDeParedes(p)
  const visto = new Set([inicio])
  const fila = [inicio]
  while (fila.length) {
    const c = fila.pop() as number
    const x = c % p.w
    const y = Math.floor(c / p.w)
    for (const [nx, ny, k] of [
      [x, y - 1, chaveDaParede(x, y, 0)],
      [x, y + 1, chaveDaParede(x, y + 1, 0)],
      [x - 1, y, chaveDaParede(x, y, 1)],
      [x + 1, y, chaveDaParede(x + 1, y, 1)],
    ] as [number, number, string][]) {
      if (nx < 0 || ny < 0 || nx >= p.w || ny >= p.h || paredes.has(k)) continue
      const n = ny * p.w + nx
      if (visto.has(n) || (p.chao[n] ?? null) !== alvo) continue
      visto.add(n)
      fila.push(n)
    }
  }
  return [...visto]
}
function redimensionar(p: Planta, w: number, h: number): Planta {
  const chao = Array.from({ length: w * h }, (_, i) => {
    const x = i % w
    const y = Math.floor(i / w)
    return x < p.w && y < p.h ? (p.chao[y * p.w + x] ?? null) : (p.chao[0] ?? 'madeira')
  })
  const paredes = p.paredes.filter(([x, y, o]) => (o === 0 ? x < w && y <= h : x <= w && y < h))
  const salas = p.salas.flatMap((s) => {
    const celulas = s.celulas.map((c) => [c % p.w, Math.floor(c / p.w)] as const)
    if (celulas.some(([x, y]) => x >= w || y >= h)) return []
    return [{ ...s, celulas: celulas.map(([x, y]) => y * w + x) }]
  })
  return { ...p, w, h, chao, paredes, salas }
}

/* ---------------------------------------------------------- o gancho --- */

type Opcoes = {
  /** A planta salva no servidor (null = andar ainda sem planta). */
  plantaDoServidor: Planta | null
  ativo: boolean
  salvar: (p: Planta) => Promise<void>
  avisar: (t: string) => void
}

export function useConstrucao({ plantaDoServidor, ativo, salvar, avisar }: Opcoes) {
  const [local, setLocal] = useState<Planta | null>(null)
  const planta = local ?? plantaDoServidor
  const [ferramenta, setFerramenta] = useState<Ferramenta>('parede')
  const [formaChao, setFormaChao] = useState<FormaChao>('pincel')
  const [formaParede, setFormaParede] = useState<FormaParede>('linha')
  const [material, setMaterial] = useState<Material | 'nada'>('carpete')
  const [corChao, setCorChao] = useState<string>('#4a5fb8')
  const [tipoParede, setTipoParede] = useState<TipoDeParede>('parede')
  const [corParede, setCorParede] = useState<string | null>(null)
  const [previa, setPrevia] = useState<Previa>(null)
  const [dialogo, setDialogo] = useState<DialogoDeSala>(null)
  const [estado, setEstado] = useState<'salvo' | 'salvando' | 'erro'>('salvo')
  const gesto = useRef<Gesto>(null)
  const antes = useRef<Planta[]>([])
  const depois = useRef<Planta[]>([])
  const fila = useRef<{ t: number; p: Planta | null }>({ t: 0, p: null })
  const [, setVersao] = useState(0)

  // fora do modo construção, o que vale é o servidor (depois de salvar tudo)
  useEffect(() => {
    if (!ativo && estado === 'salvo' && !fila.current.p) setLocal(null)
  }, [ativo, estado])

  const enviar = useCallback(
    (p: Planta) => {
      window.clearTimeout(fila.current.t)
      fila.current.p = p
      setEstado('salvando')
      fila.current.t = window.setTimeout(() => {
        const alvo = fila.current.p
        fila.current.p = null
        if (!alvo) return
        salvar(alvo)
          .then(() => setEstado(fila.current.p ? 'salvando' : 'salvo'))
          .catch(() => {
            setEstado('erro')
            avisar('Não deu para salvar a planta. Tente de novo.')
          })
      }, 650)
    },
    [salvar, avisar],
  )
  const mudar = useCallback(
    (p: Planta) => {
      if (!planta) return
      antes.current = [...antes.current.slice(-59), planta]
      depois.current = []
      setLocal(p)
      enviar(p)
    },
    [planta, enviar],
  )
  const desfazer = useCallback(() => {
    const p = antes.current.pop()
    if (!p || !planta) return
    depois.current.push(planta)
    setLocal(p)
    enviar(p)
    setVersao((v) => v + 1)
  }, [planta, enviar])
  const refazer = useCallback(() => {
    const p = depois.current.pop()
    if (!p || !planta) return
    antes.current.push(planta)
    setLocal(p)
    enviar(p)
    setVersao((v) => v + 1)
  }, [planta, enviar])

  useEffect(() => {
    if (!ativo) return
    const tecla = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || (e.target as HTMLElement).closest('input, textarea')) return
      const k = e.key.toLowerCase()
      if (k === 'z' && !e.shiftKey) {
        e.preventDefault()
        desfazer()
      } else if (k === 'y' || (k === 'z' && e.shiftKey)) {
        e.preventDefault()
        refazer()
      }
    }
    window.addEventListener('keydown', tecla)
    return () => window.removeEventListener('keydown', tecla)
  }, [ativo, desfazer, refazer])

  const codigo = material === 'nada' ? null : codigoDoChao(material, corChao)
  const celulaEm = (p: Planta, [x, y]: Pt) => (x >= 0 && y >= 0 && x < p.w && y < p.h ? Math.floor(y) * p.w + Math.floor(x) : -1)
  const vertice = (p: Planta, [x, y]: Pt): Pt => [Math.max(0, Math.min(p.w, Math.round(x))), Math.max(0, Math.min(p.h, Math.round(y)))]
  const retanguloDeCelulas = (p: Planta, a: number, b: number) => {
    const [x0, x1] = [a % p.w, b % p.w].sort((m, n) => m - n) as [number, number]
    const [y0, y1] = [Math.floor(a / p.w), Math.floor(b / p.w)].sort((m, n) => m - n) as [number, number]
    const out: number[] = []
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) out.push(y * p.w + x)
    return out
  }

  /** Ponteiro apertado no chão (unidades do tabuleiro). Devolve false se não é com a construção. */
  const apertar = (pt: Pt, tela: { x: number; y: number }): boolean => {
    if (!ativo || !planta || ferramenta === 'mao') return false
    if (ferramenta === 'chao') {
      const c = celulaEm(planta, pt)
      if (c < 0) return true
      if (formaChao === 'balde') {
        mudar(comChao(planta, balde(planta, c), codigo))
        return true
      }
      gesto.current = { tipo: 'chao', inicio: c, celulas: new Set([c]) }
      setPrevia({ celulas: [c], apagar: !codigo, cor: codigo ? (corDe(codigo) ?? MATERIAIS.find((m) => m.id === material)?.amostra) : undefined })
      return true
    }
    if (ferramenta === 'sala') {
      const c = celulaEm(planta, pt)
      if (c < 0) return true
      const ja = salaDaCelula(planta, c)
      if (ja) {
        setDialogo({ celulas: ja.celulas, x: tela.x, y: tela.y, existente: ja.lot })
        return true
      }
      const r = areaFechada(planta, c)
      if ('erro' in r) {
        avisar(r.erro)
        return true
      }
      const encosta = planta.salas.find((s) => s.celulas.some((x) => r.celulas.includes(x)))
      if (encosta) {
        avisar(`Essa área encosta na sala ${encosta.nome}. Feche com paredes ou desfaça a outra sala.`)
        return true
      }
      setPrevia({ celulas: r.celulas, cor: '#ffb020' })
      setDialogo({ celulas: r.celulas, x: tela.x, y: tela.y, existente: null })
      return true
    }
    const v = vertice(planta, pt)
    gesto.current = { tipo: ferramenta, a: v, b: v, moveu: false, ponto: pt }
    const l = linhaMaisPerto(planta, pt)
    setPrevia(l ? { paredes: [l], apagar: ferramenta === 'apagar' } : null)
    return true
  }
  const mover = (pt: Pt): boolean => {
    const g = gesto.current
    if (!g || !planta) return false
    if (g.tipo === 'chao') {
      const c = celulaEm(planta, pt)
      if (c < 0) return true
      if (formaChao === 'retangulo') setPrevia((pv) => ({ ...pv, celulas: retanguloDeCelulas(planta, g.inicio, c) }))
      else if (!g.celulas.has(c)) {
        g.celulas.add(c)
        setPrevia((pv) => ({ ...pv, celulas: [...g.celulas] }))
      }
      return true
    }
    const b = vertice(planta, pt)
    if (b[0] !== g.a[0] || b[1] !== g.a[1]) g.moveu = true
    g.b = b
    setPrevia({ paredes: g.moveu ? trecho(planta, g.a, b, formaParede === 'caixa' && g.tipo === 'parede') : [], apagar: g.tipo === 'apagar' })
    return true
  }
  const soltar = (): boolean => {
    const g = gesto.current
    gesto.current = null
    if (!g || !planta) return false
    if (g.tipo === 'chao') {
      const celulas = formaChao === 'retangulo' ? (previa?.celulas ?? [g.inicio]) : [...g.celulas]
      setPrevia(null)
      mudar(comChao(planta, celulas, codigo))
      return true
    }
    setPrevia(null)
    const linhas = g.moveu ? trecho(planta, g.a, g.b, formaParede === 'caixa' && g.tipo === 'parede') : [linhaMaisPerto(planta, g.ponto)].filter((x): x is [number, number, 0 | 1] => !!x)
    if (!linhas.length) return true
    mudar(comParedes(planta, linhas, g.tipo === 'apagar' ? null : tipoParede, corParede))
    return true
  }
  /** Passou o mouse sem apertar: mostra onde vai cair. */
  const pairar = (pt: Pt) => {
    if (!ativo || !planta || gesto.current || dialogo) return
    if (ferramenta === 'parede' || ferramenta === 'apagar') {
      const l = linhaMaisPerto(planta, pt)
      setPrevia(l ? { paredes: [l], apagar: ferramenta === 'apagar' } : null)
    } else if (ferramenta === 'chao') {
      const c = celulaEm(planta, pt)
      setPrevia(c >= 0 ? { celulas: [c], apagar: !codigo, cor: codigo ? (corDe(codigo) ?? MATERIAIS.find((m) => m.id === material)?.amostra) : undefined } : null)
    } else if (ferramenta === 'sala') {
      const c = celulaEm(planta, pt)
      const ja = c >= 0 ? salaDaCelula(planta, c) : null
      setPrevia(ja ? { celulas: ja.celulas, cor: '#ffb020' } : null)
    }
  }

  const criarSala = (nome: string, modelo: ModeloId) => {
    if (!planta || !dialogo) return
    mudar({ ...planta, salas: [...planta.salas, { lot: proximaVaga(planta), nome: nome.trim() || 'Sala nova', modelo, celulas: dialogo.celulas }] })
    setDialogo(null)
    setPrevia(null)
    avisar(`${nome.trim() || 'Sala nova'} virou sala de chamada.`)
  }
  const desfazerSala = (lot: number) => {
    if (!planta) return
    const s = planta.salas.find((x) => x.lot === lot)
    mudar({ ...planta, salas: planta.salas.filter((x) => x.lot !== lot) })
    setDialogo(null)
    setPrevia(null)
    if (s) avisar(`${s.nome} voltou a ser só uma área. A chamada dela foi encerrada.`)
  }
  const redimensionarPara = (w: number, h: number) => {
    if (!planta) return
    const novo = redimensionar(planta, Math.max(6, Math.min(48, w)), Math.max(4, Math.min(32, h)))
    if (novo.salas.length < planta.salas.length) avisar('Salas que ficaram para fora da planta deixaram de ser salas.')
    mudar(novo)
  }

  return {
    planta,
    previa,
    ferramenta,
    apertar,
    mover,
    soltar,
    pairar,
    limparPrevia: () => !gesto.current && setPrevia(null),
    gaveta: planta ? (
      <GavetaDaConstrucao
        planta={planta}
        ferramenta={ferramenta}
        setFerramenta={(f) => {
          setFerramenta(f)
          setPrevia(null)
        }}
        formaChao={formaChao}
        setFormaChao={setFormaChao}
        formaParede={formaParede}
        setFormaParede={setFormaParede}
        material={material}
        setMaterial={setMaterial}
        corChao={corChao}
        setCorChao={setCorChao}
        tipoParede={tipoParede}
        setTipoParede={setTipoParede}
        corParede={corParede}
        setCorParede={setCorParede}
        estado={estado}
        podeDesfazer={antes.current.length > 0}
        podeRefazer={depois.current.length > 0}
        onDesfazer={desfazer}
        onRefazer={refazer}
        onTamanho={redimensionarPara}
      />
    ) : null,
    dialogo:
      dialogo && planta ? (
        <DialogoDaSala
          d={dialogo}
          planta={planta}
          onFechar={() => {
            setDialogo(null)
            setPrevia(null)
          }}
          onCriar={criarSala}
          onDesfazer={desfazerSala}
        />
      ) : null,
  }
}

/* -------------------------------------------- camada por cima do mapa --- */

/** Grade, salas e a prévia do que vai ser desenhado, por cima do tabuleiro (em px do tabuleiro). */
export function CamadaDaConstrucao({ planta, ox, oy, largura, altura, previa }: { planta: Planta; ox: number; oy: number; largura: number; altura: number; previa: Previa }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current
    if (!c) return
    const K = 2
    c.width = largura * K
    c.height = altura * K
    const g = c.getContext('2d') as CanvasRenderingContext2D
    g.setTransform(K, 0, 0, K, 0, 0)
    g.clearRect(0, 0, largura, altura)
    const X = (x: number) => ox + x * 16
    const Y = (y: number) => oy + y * 13.5
    // grade
    g.lineWidth = 0.5
    g.strokeStyle = 'rgba(255,255,255,.28)'
    g.beginPath()
    for (let x = 0; x <= planta.w; x++) {
      g.moveTo(X(x), Y(0))
      g.lineTo(X(x), Y(planta.h))
    }
    for (let y = 0; y <= planta.h; y++) {
      g.moveTo(X(0), Y(y))
      g.lineTo(X(planta.w), Y(y))
    }
    g.stroke()
    g.lineWidth = 1.2
    g.setLineDash([4, 3])
    g.strokeStyle = 'rgba(255,176,32,.9)'
    g.strokeRect(X(0), Y(0), planta.w * 16, planta.h * 13.5)
    g.setLineDash([])
    // salas: chão aceso e o contorno na cor da sala; sala que abriu fica tracejada em vermelho
    planta.salas.forEach((s, i) => {
      const aberta = 'erro' in areaFechada(planta, s.celulas[0] as number)
      const cor = aberta ? '#e5484d' : (CORES_DAS_SALAS[i % CORES_DAS_SALAS.length] as string)
      const dentro = new Set(s.celulas)
      g.fillStyle = cor + '26'
      for (const cel of s.celulas) g.fillRect(X(cel % planta.w), Y(Math.floor(cel / planta.w)), 16, 13.5)
      g.strokeStyle = cor
      g.lineWidth = 1.6
      if (aberta) g.setLineDash([3, 2])
      g.beginPath()
      for (const cel of s.celulas) {
        const x = cel % planta.w
        const y = Math.floor(cel / planta.w)
        if (!dentro.has(cel - planta.w) || y === 0) (g.moveTo(X(x), Y(y)), g.lineTo(X(x + 1), Y(y)))
        if (!dentro.has(cel + planta.w) || y === planta.h - 1) (g.moveTo(X(x), Y(y + 1)), g.lineTo(X(x + 1), Y(y + 1)))
        if (x === 0 || !dentro.has(cel - 1)) (g.moveTo(X(x), Y(y)), g.lineTo(X(x), Y(y + 1)))
        if (x === planta.w - 1 || !dentro.has(cel + 1)) (g.moveTo(X(x + 1), Y(y)), g.lineTo(X(x + 1), Y(y + 1)))
      }
      g.stroke()
      g.setLineDash([])
    })
    // prévia
    if (previa?.celulas) {
      g.fillStyle = previa.apagar ? 'rgba(229,72,77,.35)' : (previa.cor ?? '#ffb020') + '99'
      for (const cel of previa.celulas) g.fillRect(X(cel % planta.w), Y(Math.floor(cel / planta.w)), 16, 13.5)
      g.strokeStyle = previa.apagar ? '#e5484d' : '#fff'
      g.lineWidth = 0.8
      for (const cel of previa.celulas) g.strokeRect(X(cel % planta.w) + 0.4, Y(Math.floor(cel / planta.w)) + 0.4, 15.2, 12.7)
    }
    if (previa?.paredes?.length) {
      g.lineCap = 'round'
      g.strokeStyle = 'rgba(20,26,51,.7)'
      g.lineWidth = 5
      g.beginPath()
      for (const [x, y, o] of previa.paredes) (g.moveTo(X(x), Y(y)), g.lineTo(o === 0 ? X(x + 1) : X(x), o === 0 ? Y(y) : Y(y + 1)))
      g.stroke()
      g.strokeStyle = previa.apagar ? '#e5484d' : '#ffd34d'
      g.lineWidth = 3
      g.stroke()
    }
  }, [planta, ox, oy, largura, altura, previa])
  return <canvas ref={ref} className="esc__realista escon-camada" style={{ width: largura, height: altura }} aria-hidden />
}

/* ------------------------------------------------------------ gaveta --- */

type GavetaProps = {
  planta: Planta
  ferramenta: Ferramenta
  setFerramenta: (f: Ferramenta) => void
  formaChao: FormaChao
  setFormaChao: (f: FormaChao) => void
  formaParede: FormaParede
  setFormaParede: (f: FormaParede) => void
  material: Material | 'nada'
  setMaterial: (m: Material | 'nada') => void
  corChao: string
  setCorChao: (c: string) => void
  tipoParede: TipoDeParede
  setTipoParede: (t: TipoDeParede) => void
  corParede: string | null
  setCorParede: (c: string | null) => void
  estado: 'salvo' | 'salvando' | 'erro'
  podeDesfazer: boolean
  podeRefazer: boolean
  onDesfazer: () => void
  onRefazer: () => void
  onTamanho: (w: number, h: number) => void
}

const FERRAMENTAS: { id: Ferramenta; nome: string; icone: string; dica: string }[] = [
  { id: 'parede', nome: 'Paredes', icone: 'fa-border-style', dica: 'Arraste pelas linhas da grade. Um clique troca só aquele trecho.' },
  { id: 'chao', nome: 'Chão', icone: 'fa-th', dica: 'Pinte os quadradinhos. O balde enche a área até as paredes.' },
  { id: 'sala', nome: 'Virar sala', icone: 'fa-door-open', dica: 'Feche uma área com paredes (porta conta) e clique dentro dela.' },
  { id: 'apagar', nome: 'Tirar parede', icone: 'fa-eraser', dica: 'Arraste ou clique nas paredes que quer tirar.' },
  { id: 'mao', nome: 'Selecionar', icone: 'fa-mouse-pointer', dica: 'Clique num móvel para virar, trocar a cor ou apagar. Arraste o móvel para mudar de lugar e o chão para andar pelo mapa.' },
]

/** Desenhinho de cada tipo de parede, visto de frente. */
function IconeParede({ tipo }: { tipo: TipoDeParede }) {
  const quadro = <rect x="2" y="3" width="28" height="18" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.4" opacity=".35" />
  return (
    <svg viewBox="0 0 32 24" width="34" height="26" aria-hidden>
      {quadro}
      {tipo === 'parede' ? <rect x="2" y="3" width="28" height="18" rx="1.5" fill="currentColor" opacity=".85" /> : null}
      {tipo === 'meia' ? (
        <>
          <rect x="2" y="15" width="28" height="6" rx="1" fill="currentColor" opacity=".85" />
          <rect x="3" y="4" width="26" height="10" fill="#7fd4ff" opacity=".55" />
        </>
      ) : null}
      {tipo === 'vidro' ? (
        <>
          <rect x="3" y="4" width="26" height="16" fill="#7fd4ff" opacity=".55" />
          <path d="M16 4v16M7 17l6-9" stroke="#fff" strokeWidth="1.2" opacity=".8" />
        </>
      ) : null}
      {tipo === 'janela' ? (
        <>
          <path d="M2 3h28v18H2z M8 7h16v9H8z" fill="currentColor" fillRule="evenodd" opacity=".85" />
          <rect x="8" y="7" width="16" height="9" fill="#7fd4ff" opacity=".55" />
        </>
      ) : null}
      {tipo === 'porta' ? (
        <>
          <path d="M2 3h28v18h-8V9H10v12H2z" fill="currentColor" opacity=".85" />
          <path d="M10 21V9h12v12" fill="none" stroke="#a8744a" strokeWidth="1.6" />
        </>
      ) : null}
    </svg>
  )
}

function GavetaDaConstrucao(p: GavetaProps) {
  const dica = FERRAMENTAS.find((f) => f.id === p.ferramenta)?.dica
  const mat = MATERIAIS.find((m) => m.id === p.material)
  return (
    <div className="escon">
      <div className="escon-ferramentas" role="toolbar" aria-label="Ferramentas da construção">
        {FERRAMENTAS.map((f) => (
          <button key={f.id} type="button" className={`escon-ferr${p.ferramenta === f.id ? ' escon-ferr--on' : ''}`} aria-pressed={p.ferramenta === f.id} onClick={() => p.setFerramenta(f.id)}>
            <i className={`fas ${f.icone}`} aria-hidden />
            <span>{f.nome}</span>
          </button>
        ))}
        <span className="escon-sep" aria-hidden />
        <button type="button" className="escon-mini" onClick={p.onDesfazer} disabled={!p.podeDesfazer} aria-label="Desfazer (Ctrl+Z)" title="Desfazer (Ctrl+Z)">
          <i className="fas fa-undo" aria-hidden />
        </button>
        <button type="button" className="escon-mini" onClick={p.onRefazer} disabled={!p.podeRefazer} aria-label="Refazer (Ctrl+Y)" title="Refazer (Ctrl+Y)">
          <i className="fas fa-redo" aria-hidden />
        </button>
        <span className={`escon-estado escon-estado--${p.estado}`} role="status">
          {p.estado === 'salvando' ? 'Salvando…' : p.estado === 'erro' ? 'Não salvou' : 'Tudo salvo'}
        </span>
      </div>

      <div className="escon-opcoes">
        {p.ferramenta === 'parede' ? (
          <>
            <div className="escon-grupo" role="radiogroup" aria-label="Tipo de parede">
              {TIPOS_DE_PAREDE.map((t) => (
                <button key={t.id} type="button" role="radio" aria-checked={p.tipoParede === t.id} className={`escon-tipo${p.tipoParede === t.id ? ' escon-tipo--on' : ''}`} title={t.dica} onClick={() => p.setTipoParede(t.id)}>
                  <IconeParede tipo={t.id} />
                  <span>{t.nome}</span>
                </button>
              ))}
            </div>
            <div className="escon-grupo escon-grupo--col">
              <span className="escon-rotulo">Desenhar</span>
              <div className="escon-alternar" role="radiogroup" aria-label="Como desenhar">
                <button type="button" role="radio" aria-checked={p.formaParede === 'linha'} className={p.formaParede === 'linha' ? 'on' : ''} onClick={() => p.setFormaParede('linha')}>
                  Linha
                </button>
                <button type="button" role="radio" aria-checked={p.formaParede === 'caixa'} className={p.formaParede === 'caixa' ? 'on' : ''} onClick={() => p.setFormaParede('caixa')}>
                  Sala inteira
                </button>
              </div>
            </div>
            {p.tipoParede !== 'vidro' ? (
              <div className="escon-grupo escon-grupo--col">
                <span className="escon-rotulo">Cor da parede</span>
                <div className="escon-cores">
                  <button type="button" className={`escon-cor escon-cor--natural${p.corParede === null ? ' on' : ''}`} aria-label="Cor natural" title="Natural" onClick={() => p.setCorParede(null)} />
                  {CORES_DE_PAREDE.map((c) => (
                    <button key={c} type="button" className={`escon-cor${p.corParede === c ? ' on' : ''}`} style={{ background: c }} aria-label={`Cor ${c}`} onClick={() => p.setCorParede(c)} />
                  ))}
                  <label className="escon-cor escon-cor--outra" title="Outra cor">
                    <i className="fas fa-plus" aria-hidden />
                    <input type="color" value={p.corParede ?? '#f1ede6'} onChange={(e) => p.setCorParede(e.target.value)} />
                  </label>
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        {p.ferramenta === 'chao' ? (
          <>
            <div className="escon-grupo" role="radiogroup" aria-label="Material do chão">
              {MATERIAIS.map((m) => (
                <button key={m.id} type="button" role="radio" aria-checked={p.material === m.id} className={`escon-mat${p.material === m.id ? ' escon-mat--on' : ''}`} onClick={() => p.setMaterial(m.id)}>
                  <i className={`escon-amostra escon-amostra--${m.id}`} style={{ background: m.tinge ? p.corChao : m.amostra }} />
                  <span>{m.nome}</span>
                </button>
              ))}
              <button type="button" role="radio" aria-checked={p.material === 'nada'} className={`escon-mat${p.material === 'nada' ? ' escon-mat--on' : ''}`} onClick={() => p.setMaterial('nada')}>
                <i className="escon-amostra escon-amostra--nada" />
                <span>Sem chão</span>
              </button>
            </div>
            <div className="escon-grupo escon-grupo--col">
              <span className="escon-rotulo">Pintar</span>
              <div className="escon-alternar" role="radiogroup" aria-label="Como pintar">
                {(
                  [
                    ['pincel', 'Pincel'],
                    ['retangulo', 'Retângulo'],
                    ['balde', 'Balde'],
                  ] as const
                ).map(([id, nome]) => (
                  <button key={id} type="button" role="radio" aria-checked={p.formaChao === id} className={p.formaChao === id ? 'on' : ''} onClick={() => p.setFormaChao(id)}>
                    {nome}
                  </button>
                ))}
              </div>
            </div>
            {mat?.tinge ? (
              <div className="escon-grupo escon-grupo--col">
                <span className="escon-rotulo">Cor do {mat.nome.toLowerCase()}</span>
                <div className="escon-cores">
                  {CORES_DE_CHAO.map((c) => (
                    <button key={c} type="button" className={`escon-cor${p.corChao === c ? ' on' : ''}`} style={{ background: c }} aria-label={`Cor ${c}`} onClick={() => p.setCorChao(c)} />
                  ))}
                  <label className="escon-cor escon-cor--outra" title="Outra cor">
                    <i className="fas fa-plus" aria-hidden />
                    <input type="color" value={p.corChao} onChange={(e) => p.setCorChao(e.target.value)} />
                  </label>
                </div>
              </div>
            ) : null}
          </>
        ) : null}

        {p.ferramenta === 'sala' ? (
          <div className="escon-passos">
            <span>
              <b>1</b> Feche a área com paredes. Porta, vidro e janela também fecham.
            </span>
            <span>
              <b>2</b> Clique dentro dela e dê um nome. Ela vira sala de chamada, com cadeado e equipe como as outras.
            </span>
            <span>
              <b>3</b> Clique numa sala que já existe para desfazer.
            </span>
          </div>
        ) : null}

        <div className="escon-grupo escon-grupo--col escon-tamanho">
          <span className="escon-rotulo">Tamanho do andar</span>
          <div className="escon-medidas">
            <label>
              <span>Largura</span>
              <input type="number" min={6} max={48} value={p.planta.w} onChange={(e) => p.onTamanho(Number(e.target.value) || p.planta.w, p.planta.h)} />
            </label>
            <span aria-hidden>×</span>
            <label>
              <span>Fundo</span>
              <input type="number" min={4} max={32} value={p.planta.h} onChange={(e) => p.onTamanho(p.planta.w, Number(e.target.value) || p.planta.h)} />
            </label>
          </div>
        </div>
      </div>
      <p className="escon-dica">{dica}</p>
    </div>
  )
}

/* ---------------------------------------------------- virar sala --- */

function DialogoDaSala({ d, planta, onFechar, onCriar, onDesfazer }: { d: NonNullable<DialogoDeSala>; planta: Planta; onFechar: () => void; onCriar: (nome: string, modelo: ModeloId) => void; onDesfazer: (lot: number) => void }) {
  const existente = d.existente !== null ? planta.salas.find((s) => s.lot === d.existente) : null
  const [nome, setNome] = useState(`Sala ${planta.salas.length + 1}`)
  const [modelo, setModelo] = useState<ModeloId>('reuniao')
  const campo = useRef<HTMLInputElement>(null)
  useEffect(() => campo.current?.select(), [])
  const modelos = useMemo(() => Object.keys(MODELOS) as ModeloId[], [])
  return (
    <div className="esc__janela escon-sala" style={{ left: `min(${d.x}px, calc(100% - 316px))`, top: `min(${d.y}px, calc(100% - 430px))` }} role="dialog" aria-label={existente ? existente.nome : 'Nova sala'}>
      <header>
        <b>{existente ? existente.nome : 'Nova sala'}</b>
        <button type="button" className="esc__icone-botao" aria-label="Fechar" onClick={onFechar}>
          <i className="fas fa-times" aria-hidden />
        </button>
      </header>
      {existente ? (
        <>
          <p>
            Esta área é uma sala de chamada ({d.celulas.length} quadradinhos). Para trocar o nome, o cadeado ou a equipe, use o cartão da sala fora da construção.
          </p>
          <button type="button" className="esc__botao esc__botao--apagar" onClick={() => onDesfazer(existente.lot)}>
            <i className="fas fa-unlink" aria-hidden /> Deixar de ser sala
          </button>
        </>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onCriar(nome, modelo)
          }}
        >
          <p>{d.celulas.length} quadradinhos fechados. Escolha o nome e o cenário da reunião.</p>
          <label className="escon-campo">
            <span>Nome</span>
            <input ref={campo} value={nome} maxLength={40} onChange={(e) => setNome(e.target.value)} />
          </label>
          <span className="escon-rotulo">Cenário da chamada</span>
          <div className="escon-cenarios" role="radiogroup">
            {modelos.map((m) => (
              <button key={m} type="button" role="radio" aria-checked={modelo === m} className={modelo === m ? 'on' : ''} onClick={() => setModelo(m)}>
                <img src={`/escritorio/realista/fundos/${m}.jpg?v=1`} alt="" loading="lazy" />
                <span>{NOMES_DOS_MODELOS[m]}</span>
              </button>
            ))}
          </div>
          <button type="submit" className="esc__botao">
            <i className="fas fa-door-open" aria-hidden /> Virar sala de chamada
          </button>
        </form>
      )}
    </div>
  )
}

/* ------------------------------------------------ miniatura da planta --- */

const AMOSTRAS: Record<string, string> = Object.fromEntries(MATERIAIS.map((m) => [m.id, m.amostra]))

/** Desenho da planta visto de cima, para escolher o modelo. */
export function miniaturaDaPlanta(p: Planta, largura = 220): string {
  const k = largura / p.w
  const c = document.createElement('canvas')
  c.width = largura
  c.height = Math.round(p.h * k)
  const g = c.getContext('2d') as CanvasRenderingContext2D
  g.fillStyle = '#1a2140'
  g.fillRect(0, 0, c.width, c.height)
  p.chao.forEach((cod, i) => {
    if (!cod) return
    g.fillStyle = corDe(cod) ?? AMOSTRAS[materialDe(cod) ?? ''] ?? '#ccc'
    g.fillRect((i % p.w) * k, Math.floor(i / p.w) * k, k + 0.5, k + 0.5)
  })
  for (const [x, y, o, t] of p.paredes) {
    g.strokeStyle = t === 'vidro' || t === 'meia' ? '#7fd4ff' : t === 'porta' ? '#ffb020' : '#2b2f3a'
    g.lineWidth = t === 'porta' ? 1.6 : 2.2
    g.beginPath()
    g.moveTo(x * k, y * k)
    g.lineTo((o === 0 ? x + 1 : x) * k, (o === 0 ? y : y + 1) * k)
    g.stroke()
  }
  return c.toDataURL()
}
